import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, first, of, tap, throwError } from "rxjs";
import { InsightsService } from "src/app/shared/services/insights.service";
import { SnackBarService } from "src/app/shared/services/snack-bar.service";
import { UserService } from "../services/user.service";
import { environment } from "src/environments/environment";

/** Job endpoints of the BU document upload; their 403/404/409 answers are explained on the page itself. */
export const CALLER_HANDLED_PATH: string = "/api/Document/esra1/uploads";
export const CALLER_HANDLED_STATUSES: ReadonlySet<number> = new Set<number>([403, 404, 409]);

/** Page loads and document opens whose 403 (business unit not accessible) the caller explains itself: pipe class by id/name, valve by id, ESRA1 document. */
export const PAGE_HANDLED_FORBIDDEN_URL: RegExp = /\/(api\/PipeClass\/[^/?]+|GetValveViewModelById\/[^/?]+|api\/Document\/esra1\/p[pv]\/[^/?]+)(\?.*)?$/;

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor{

    constructor(private snackBarService: SnackBarService,
        private insightsService: InsightsService,
        private userService: UserService){}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                // 400 Bad Request errors carry a user-readable validation message from the
                // backend; let the calling component read and display it instead of showing
                // the generic fallback here. The same goes for the upload job endpoints: an expired
                // upload (404), a changed upload or structured confirmation failure (409) and a missing
                // permission (403) are shown by the upload page with their real explanation.
                const isHandledByCaller: boolean = (request.url.includes(CALLER_HANDLED_PATH) && CALLER_HANDLED_STATUSES.has(error.status))
                    || (request.method === 'GET' && error.status === 403 && PAGE_HANDLED_FORBIDDEN_URL.test(request.url));
                if (error.status !== 400 && !isHandledByCaller) {
                    this.snackBarService.showError("The application encountered an error and cannot complete your request. Please try again later or use the Contact Us page for assistance");
                }

                this.blobToText(error.error).pipe(first(),
                    catchError(err=> of(this.sendError(error, error.error))),
                    tap((text) => {
                        this.sendError(error, text);
                    })).subscribe();

                return throwError(() => error);
            })
        )
    }

    private sendError(error: HttpErrorResponse, text) {
        const ex: Error =  {
            name : error.name,
            message : error.message + '\nError occured For user: ' + this.userService.getCurrentUser().name + ' (' + this.userService.getCurrentUser().username + ') ',
            stack : text,
        }
        this.insightsService.logException(ex, 1);
        if(environment['isLocal']) {
            console.error(ex.message, ex.stack);
        }
    }

    /* Method borrowed from swagger autogen*/
    private blobToText(blob: any): Observable<string> {
        return new Observable<string>((observer: any) => {
            if (!blob) {
                observer.next("");
                observer.complete();
            } else {
                const reader = new FileReader();
                reader.onload = event => {
                    observer.next((event.target as any).result);
                    observer.complete();
                };
                reader.readAsText(blob);
            }
        });
    }

}
